import React from "react";
import { useNavigate } from "react-router-dom";
import { Upload } from "lucide-react";
import * as api from "../features/bpm/bpmApi";
import BpmDiagram from "../features/bpm/BpmDiagram";
import {
  Dropzone, FormatGuide, ImportProgress, ImportResultCard, ImportsTable,
} from "../features/bpm/ImportParts";
import { card, Err, Loading } from "./cyberShared";

/**
 * BPM Optimization → Import Process.
 *
 * Brings an existing process model into Process Analysis: BPMN 2.0 files are taken
 * as-is, Visio swimlanes are converted, and PDF/PNG/JPG flowcharts are read by
 * Claude on Bedrock and rebuilt as BPMN (backend/apis/bpm_import.py). The result
 * card shows confidence, what the converter was unsure about and a rendered
 * preview before the user sends it to analysis.
 *
 * `onAnalyze(id)` is supplied by the dashboard shell; on the standalone route the
 * page navigates to /bpm?process=<id> instead.
 */
export default function BpmImportPage({ onAnalyze }) {
  const navigate = useNavigate();
  const [busy, setBusy] = React.useState(null); // {file, startedAt}
  const [error, setError] = React.useState("");
  const [result, setResult] = React.useState(null);
  const [preview, setPreview] = React.useState({ loading: false, xml: null, error: "" });
  const [imports, setImports] = React.useState({ loading: true, items: [], error: "" });
  const [deleting, setDeleting] = React.useState(null);

  const loadImports = React.useCallback(() => {
    setImports((s) => ({ ...s, loading: true }));
    api.listImports()
      .then((d) => setImports({ loading: false, items: d.imports || [], error: "" }))
      .catch((e) => setImports({ loading: false, items: [], error: e.message || String(e) }));
  }, []);
  React.useEffect(loadImports, [loadImports]);

  const analyze = (id) => {
    if (onAnalyze) onAnalyze(id);
    else navigate(`/bpm?process=${encodeURIComponent(id)}`);
  };

  const upload = async (file) => {
    setError("");
    setResult(null);
    setPreview({ loading: false, xml: null, error: "" });
    setBusy({ file, startedAt: Date.now() });
    try {
      const r = await api.importProcess(file);
      setResult(r);
      loadImports();
      setPreview({ loading: true, xml: null, error: "" });
      api.getProcess(r.id)
        .then((p) => setPreview({ loading: false, xml: p.xml, error: "" }))
        .catch((e) => setPreview({ loading: false, xml: null, error: e.message || String(e) }));
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the imported process “${row.name}”?`)) return;
    setDeleting(row.id);
    try {
      await api.deleteImport(row.id);
      if (result?.id === row.id) setResult(null);
      loadImports();
    } catch (e) {
      setImports((s) => ({ ...s, error: e.message || String(e) }));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div style={{ color: "#e8eef5" }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#e8eef5", margin: 0,
                     display: "flex", alignItems: "center", gap: 9 }}>
          <Upload size={20} style={{ color: "#9d80ff" }} /> Import Process
        </h1>
        <p style={{ fontSize: 13, color: "#8aa0b6", margin: "6px 0 0", maxWidth: 820 }}>
          Bring an existing process model into BPM Optimization — from a modelling tool, a Visio
          swimlane chart, or just a picture of a flowchart — then analyse where it loses time and
          money. Imported models can be downloaded as clean BPMN 2.0 for Camunda, Signavio and others.
        </p>
      </div>

      <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ flex: "3 1 560px", minWidth: 0, display: "flex", flexDirection: "column", gap: 14 }}>
          {!busy && !result && <Dropzone onFile={upload} disabled={!!busy} />}
          {busy && <ImportProgress file={busy.file} startedAt={busy.startedAt} />}
          {error && (
            <div>
              <Err><b>Import failed.</b> {error}</Err>
            </div>
          )}
          {result && (
            <ImportResultCard
              result={result}
              onAnalyze={analyze}
              onDismiss={() => { setResult(null); setError(""); }}
              preview={
                preview.loading ? <div style={{ ...card }}><Loading /></div>
                : preview.error ? <Err>{preview.error}</Err>
                : preview.xml ? <BpmDiagram xml={preview.xml} height={380} />
                : null
              } />
          )}
        </div>
        <div style={{ flex: "2 1 340px", minWidth: 300, maxWidth: 520 }}>
          <FormatGuide />
        </div>
      </div>

      <ImportsTable items={imports.items} loading={imports.loading} error={imports.error}
                    onAnalyze={analyze} onDelete={remove} deleting={deleting} />
    </div>
  );
}
